import React, { useState } from 'react';
import { Zap } from 'lucide-react';
import FileUpload from '../components/common/FileUpload';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import TaskProgressPanel from '../components/conversion/TaskProgressPanel';
import ConversionHistory from '../components/conversion/ConversionHistory';
import { useConversionProcess } from '../hooks/useConversionProcess';

const CompressPDF: React.FC = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [compressionLevel, setCompressionLevel] = useState<1 | 2 | 3>(2);
    const [refreshKey, setRefreshKey] = useState(0);

    const {
        taskId,
        isCreating,
        processError,
        setProcessError,
        executeTask,
        resetTask,
    } = useConversionProcess();

    const handleFilesSelected = (selectedFiles: File[]) => {
        setFiles(selectedFiles);
        setProcessError(null);
    };

    const handleCompress = async () => {
        if (files.length === 0) {
            setProcessError('请选择一个 PDF 文件');
            return;
        }

        await executeTask(files[0], 'compress', { optimizeLevel: compressionLevel });
    };

    const handleReset = () => {
        resetTask();
        setFiles([]);
    };

    const compressionLevels = [
        { value: 1, label: '低压缩', description: '文件较大，质量最高' },
        { value: 2, label: '中等压缩', description: '平衡文件大小和质量' },
        { value: 3, label: '高压缩', description: '文件最小，质量较低' },
    ];

    return (
        <div className="compress-page">
            <div className="container">
                <div className="page-header">
                    <h1>压缩 PDF 文件</h1>
                    <p>减小 PDF 文件大小，优化存储和传输</p>
                </div>

                <div className="page-content">
                    <Card glass>
                        {!taskId ? (
                            <>
                                <div className="upload-section">
                                    <FileUpload
                                        onFilesSelected={handleFilesSelected}
                                        multiple={false}
                                        maxFiles={1}
                                    />
                                </div>

                                {files.length > 0 && (
                                    <>
                                        <div className="form-group">
                                            <label className="form-label">
                                                压缩级别
                                            </label>
                                            <div className="compression-options">
                                                {compressionLevels.map((level) => (
                                                    <label
                                                        key={level.value}
                                                        className={`compression-option ${compressionLevel === level.value ? 'selected' : ''}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="compression"
                                                            value={level.value}
                                                            checked={compressionLevel === level.value}
                                                            onChange={() => setCompressionLevel(level.value as 1 | 2 | 3)}
                                                            disabled={isCreating}
                                                        />
                                                        <div className="option-content">
                                                            <strong>{level.label}</strong>
                                                            <span>{level.description}</span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="action-section">
                                            <div className="file-count">
                                                原始大小: <strong>{(files[0].size / 1024 / 1024).toFixed(2)} MB</strong>
                                            </div>

                                            <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={handleCompress}
                                                isLoading={isCreating}
                                                icon={<Zap />}
                                                disabled={isCreating}
                                            >
                                                {isCreating ? '正在准备压缩...' : '开始压缩'}
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {processError && (
                                    <div className="message message-error" style={{ marginTop: '15px' }}>
                                        {processError}
                                    </div>
                                )}
                            </>
                        ) : (
                            <TaskProgressPanel taskId={taskId} onReset={handleReset} onComplete={() => setRefreshKey(prev => prev + 1)} />
                        )}
                    </Card>

                    <div className="tips-section">
                        <Card>
                            <h3>💡 使用提示</h3>
                            <ul>
                                <li>压缩会减小文件大小，但可能影响图片质量</li>
                                <li>建议先使用中等压缩，根据效果调整</li>
                                <li>高压缩适合文本为主的 PDF</li>
                                <li>压缩后的文件大时，处理可能会需要一段时间，请耐心等待</li>
                            </ul>
                        </Card>
                    </div>

                    <ConversionHistory operationFilter="compress" onRefreshTrigger={refreshKey} />
                </div>
            </div>
        </div>
    );
};

export default CompressPDF;
